import { parseDbTimestamp } from "@repo/shared";
import { getPEPSourceName, getSanctionSourceName } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./trapets-lookup-result.i18n";
import type { TrapetsLookupSnapshot } from "./trapets-lookup-widget";

type TrapetsLookupResultProps = {
  snapshot: TrapetsLookupSnapshot;
  onRerunSearch: () => void;
  isPending: boolean;
  disabled: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  lang: string;
};

export function TrapetsLookupResult({
  snapshot,
  onRerunSearch,
  isPending,
  disabled,
  style = "none",
  title,
  description,
  lang,
}: TrapetsLookupResultProps) {
  const t = useT(T);
  const getContainerClass = () => {
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  const {
    status,
    input,
    searchedAt,
    rawResults,
    isTestMode,
    hasBeenChecked,
    clientType: snapshotClientType,
  } = snapshot;
  const snapshotIsCompany = snapshotClientType === "Company" || snapshotClientType === "Group";
  // QL-21 follow-up — for a Company/Group the PEP answer comes from the
  // REPRESENTATIVE's search. The first cut simply omitted the PEP row here,
  // which was honest about the entity search but left the advisor with no way
  // to see that the question had gone unanswered.
  const { representativeStatus, subject } = snapshot;
  const pepStatus = snapshotIsCompany ? (representativeStatus ?? null) : status;
  const pepUnassessed = snapshotIsCompany && (!subject || subject.kind === "none" || !pepStatus);
  // QL-654 — several representatives were screened: name them all.
  const perRepresentative = snapshot.representatives ?? [];
  const severalRepresentatives = perRepresentative.length > 1;
  const repName = severalRepresentatives
    ? perRepresentative
        .map((r) => r.subject.name)
        .filter(Boolean)
        .join(", ")
    : subject?.kind === "representative"
      ? subject.name
      : null;
  const isGroupSnapshot = snapshotClientType === "Group";
  // QL-21 follow-up — who each finding is ABOUT. The two are different parties
  // for a Company: PEP is the representative's alone, while sanctions are the
  // union of the entity search and the representative's. A Group has no entity
  // of its own to search, so both findings are the representative's.
  const pepSubject = repName ? `${repName} (${t.subjectRepresentative})` : input.name;
  const sanctionSubject = !snapshotIsCompany
    ? input.name
    : !repName
      ? input.name
      : isGroupSnapshot
        ? `${repName} (${t.subjectRepresentative})`
        : `${input.name} + ${repName}`;
  const hasMatches = rawResults.length > 0 || (snapshot.representativeRawResults?.length ?? 0) > 0;
  const needsReScreening = hasBeenChecked === false;

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader
        title={title}
        description={description}
        titleAddon={isTestMode ? <Badge variant="warning">{t.test}</Badge> : undefined}
      />

      {/* Re-screening required banner — previous result stays visible below, but the
          component counts as incomplete until a fresh production screening is run. */}
      {needsReScreening && (
        <div className="rounded-md border border-warning bg-warning/10 p-3 space-y-2">
          <div className="flex items-center gap-2 text-warning font-medium">
            <span className="text-lg">⚠</span>
            {t.reScreeningRequired}
          </div>
          <p className="text-sm text-muted-foreground">{t.reScreeningExplanation}</p>
          {!disabled && (
            <Button variant="default" size="sm" onClick={onRerunSearch} disabled={isPending}>
              {t.rerunSearch}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {!hasMatches && (
          <div className="flex items-center gap-2 text-success font-medium">
            <span className="text-lg">✓</span>
            {snapshotIsCompany ? t.checkedNoSanctionMatches : t.checkedNoMatches}
          </div>
        )}

        {/* Each row names its own subject: for a Company the PEP answer is the
            representative's alone, while sanctions cover the company and the
            representative together. Leaving that to inference was the whole
            confusion this replaces. */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium w-24">{t.pep}:</span>
            {pepUnassessed ? (
              <Badge variant="warning">{t.pepNotAssessed}</Badge>
            ) : (
              <Badge variant={pepStatus?.isPEP ? "warning" : "success"}>
                {pepStatus?.isPEP ? t.yes : t.no}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">{pepSubject}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium w-24">{t.sanctioned}:</span>
            <Badge
              variant={status.isSanctioned || pepStatus?.isSanctioned ? "destructive" : "success"}
            >
              {status.isSanctioned || pepStatus?.isSanctioned ? t.yes : t.no}
            </Badge>
            <span className="text-xs text-muted-foreground">{sanctionSubject}</span>
          </div>
        </div>

        {/* QL-654 — every representative's own answer, so a combined "Yes" can be
            traced to the person it is about. */}
        {severalRepresentatives && (
          <div className="space-y-2 border-t pt-3">
            <span className="text-sm font-medium">{t.representativeResultHeading}</span>
            {perRepresentative.map((rep) => (
              <div
                key={`${rep.subject.name}-${rep.subject.ssn ?? rep.subject.dob ?? ""}`}
                className="bg-background p-2 rounded border flex flex-wrap items-center gap-x-3 gap-y-1"
              >
                <span className="text-sm font-medium">{rep.subject.name}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {t.pep}:
                  <Badge variant={rep.status?.isPEP ? "warning" : "success"}>
                    {rep.status?.isPEP ? t.yes : t.no}
                  </Badge>
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {t.sanctioned}:
                  <Badge variant={rep.status?.isSanctioned ? "destructive" : "success"}>
                    {rep.status?.isSanctioned ? t.yes : t.no}
                  </Badge>
                </span>
              </div>
            ))}
          </div>
        )}

        {pepUnassessed && (
          <div className="text-xs text-warning border-t pt-3">{t.noRepresentativeHint}</div>
        )}

        {/* Matched individuals — the representative's for a Company/Group. */}
        {(pepStatus?.matchedIndividuals.length ?? 0) > 0 && (
          <div className="text-sm border-t pt-3 mt-3">
            <span className="font-medium">
              {(pepStatus?.matchedIndividuals.length ?? 0) > 1
                ? t.matchedIndividuals
                : t.matchedIndividual}
              :
            </span>
            <div className="mt-2 space-y-2">
              {(pepStatus?.matchedIndividuals ?? []).map((ind) => (
                <div
                  key={`${ind.name}-${ind.birthDate}-${ind.listType}`}
                  className="bg-background p-2 rounded border"
                >
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {ind.name && (
                      <span>
                        {t.nameLabel} <span className="text-foreground">{ind.name}</span>
                      </span>
                    )}
                    {ind.birthDate && (
                      <span>
                        {t.birthLabel} <span className="text-foreground">{ind.birthDate}</span>
                      </span>
                    )}
                    {ind.countries.length > 0 && (
                      <span>
                        {t.countryLabel}{" "}
                        <span className="text-foreground">{ind.countries.join(", ")}</span>
                      </span>
                    )}
                    <span>
                      {t.typeLabel}{" "}
                      <span
                        className={
                          ind.listType === "SANCTION"
                            ? "text-destructive"
                            : ind.listType === "PEP"
                              ? "text-warning"
                              : "text-foreground"
                        }
                      >
                        {ind.listType}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matched entities (Company) */}
        {status.matchedEntities && status.matchedEntities.length > 0 && (
          <div className="text-sm border-t pt-3 mt-3">
            <span className="font-medium">
              {status.matchedEntities.length > 1 ? t.matchedEntities : t.matchedEntity}:
            </span>
            <div className="mt-2 space-y-2">
              {status.matchedEntities.map((ent) => (
                <div
                  key={`${ent.name}-${ent.corporateId}-${ent.listType}`}
                  className="bg-background p-2 rounded border"
                >
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {ent.name && (
                      <span>
                        {t.nameLabel} <span className="text-foreground">{ent.name}</span>
                      </span>
                    )}
                    {ent.corporateId && (
                      <span>
                        {t.orgLabel} <span className="text-foreground">{ent.corporateId}</span>
                      </span>
                    )}
                    {ent.countries.length > 0 && (
                      <span>
                        {t.countryLabel}{" "}
                        <span className="text-foreground">{ent.countries.join(", ")}</span>
                      </span>
                    )}
                    <span>
                      {t.typeLabel}{" "}
                      <span
                        className={
                          ent.listType === "SANCTION" ? "text-destructive" : "text-foreground"
                        }
                      >
                        {ent.listType}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PEP details — the representative's for a Company/Group. */}
        {pepStatus?.isPEP && (pepStatus.pepSources.length > 0 || pepStatus.pepRoles.length > 0) && (
          <div className="text-sm space-y-2 border-t pt-3">
            {pepStatus.pepSources.length > 0 && (
              <div>
                <span className="font-medium text-warning">
                  {pepStatus.pepSources.length > 1 ? t.pepSources : t.pepSource}:
                </span>
                <ul className="mt-1 list-disc list-inside text-muted-foreground">
                  {pepStatus.pepSources.map((source) => (
                    <li key={source}>{getPEPSourceName(source)}</li>
                  ))}
                </ul>
              </div>
            )}
            {pepStatus.pepRoles.length > 0 && (
              <div>
                <span className="font-medium text-warning">
                  {pepStatus.pepRoles.length > 1 ? t.roles : t.role}:
                </span>
                <ul className="mt-1 list-disc list-inside text-muted-foreground">
                  {pepStatus.pepRoles.map((role) => (
                    <li key={`${role.title}-${role.roleType}`}>
                      {role.title}
                      {role.roleType && ` (${role.roleType})`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Sanction details */}
        {status.isSanctioned && status.sanctionSources.length > 0 && (
          <div className="text-sm border-t pt-3">
            <span className="font-medium text-destructive">
              {status.sanctionSources.length > 1 ? t.sanctionSources : t.sanctionSource}:
            </span>
            <ul className="mt-1 list-disc list-inside text-muted-foreground">
              {status.sanctionSources.map((source) => (
                <li key={source}>{getSanctionSourceName(source)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Search metadata */}
        <div className="text-xs text-muted-foreground border-t pt-3 flex items-center justify-between">
          <span>
            {t.search}: {input.name}
            {input.orgId && `, ${input.orgId}`}
            {input.ssnWithPrefix && `, ${input.ssnWithPrefix}`}
            {input.birthDate && `, ${input.birthDate}`}
            {" • "}
            <TimeAgoTooltip date={parseDbTimestamp(searchedAt)}>
              {getRelativeTime(searchedAt, lang)}
            </TimeAgoTooltip>
          </span>
          {!disabled && (
            <Button variant="outline" size="sm" onClick={onRerunSearch} disabled={isPending}>
              {t.rerunSearch}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
